/**
 * Token state for the platform realm.
 *
 * This module is deliberately a leaf: it imports nothing from the app. The
 * HTTP client needs the current access token on every request and the auth
 * store needs to write it after a login — if either owned the state the other
 * would have to import it, and `client -> store -> api -> client` is a cycle
 * that only shows up as an undefined import at runtime. Both depend on this
 * instead, and nothing here depends on them.
 *
 * The refresh function itself is *installed* by the auth store
 * (`setRefresher`) rather than implemented here, so the single-flight guard
 * can live next to the request path (see `client.ts`) while the actual
 * `POST /auth/refresh` call stays with the code that owns session state.
 */

import { ref, type Ref } from 'vue'

const STORAGE_KEY = 'erpx.admin.session'

export interface StoredSession {
  accessToken: string
  refreshToken: string
  /** ISO-8601 instant the access token stops being accepted. */
  expiresAt: string
}

/**
 * localStorage rather than memory: an operator who reloads the console
 * mid-incident should not be thrown back to the login screen. The refresh
 * token is rotated on every use and revocable from the server (§6), which is
 * what bounds the exposure — and this realm additionally requires MFA, so a
 * stolen token alone cannot be used to enrol a new authenticator.
 */
function read(): StoredSession | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as StoredSession).accessToken === 'string' &&
      typeof (parsed as StoredSession).refreshToken === 'string' &&
      typeof (parsed as StoredSession).expiresAt === 'string'
    ) {
      return parsed as StoredSession
    }
    return null
  } catch {
    return null
  }
}

function write(value: StoredSession | null): void {
  if (typeof localStorage === 'undefined') return
  try {
    if (value === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage blocked (private window, partitioned storage). The session still
    // works for this tab; it just will not survive a reload.
  }
}

export const session: Ref<StoredSession | null> = ref(read())

export function setSession(value: StoredSession | null): void {
  session.value = value
  write(value)
}

export function accessToken(): string | null {
  return session.value?.accessToken ?? null
}

/** Refresh this many milliseconds before the token actually expires. */
const REFRESH_SKEW_MS = 60_000

export function expiresSoon(now = Date.now()): boolean {
  const current = session.value
  if (current === null) return false
  const at = Date.parse(current.expiresAt)
  if (Number.isNaN(at)) return true
  return at - now <= REFRESH_SKEW_MS
}

/** Returns the new access token, or null when the session is unrecoverable. */
export type Refresher = () => Promise<string | null>

let refresher: Refresher | null = null

export function setRefresher(fn: Refresher | null): void {
  refresher = fn
}

export function getRefresher(): Refresher | null {
  return refresher
}

/** Called when the server says this realm requires MFA enrolment first (§6). */
export type MfaRequiredHandler = () => void

let mfaRequiredHandler: MfaRequiredHandler | null = null

export function setMfaRequiredHandler(fn: MfaRequiredHandler | null): void {
  mfaRequiredHandler = fn
}

export function notifyMfaRequired(): void {
  mfaRequiredHandler?.()
}

/** Called when a refresh fails and the operator has to sign in again. */
export type SessionLostHandler = () => void

let sessionLostHandler: SessionLostHandler | null = null

export function setSessionLostHandler(fn: SessionLostHandler | null): void {
  sessionLostHandler = fn
}

export function notifySessionLost(): void {
  sessionLostHandler?.()
}

/** Test seam: drop every installed hook and the stored session. */
export function resetSession(): void {
  setSession(null)
  refresher = null
  mfaRequiredHandler = null
  sessionLostHandler = null
}
